import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const generateSecurePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  let pwd = '';
  for (let i = 0; i < 12; i++) {
    pwd += chars?.charAt(Math.floor(Math.random() * chars?.length));
  }
  return pwd;
};

/**
 * AdminResetPasswordModal
 *
 * Props:
 *  - client: { id, nombre, email, telefono, portal_user_id }
 *  - portalAccessRequestId: string | null  (optional — for accesos-portal flow)
 *  - companyId: string
 *  - performedBy: { id, nombre }
 *  - onClose: () => void
 *  - onSuccess: (result) => void
 */
const AdminResetPasswordModal = ({
  client,
  portalAccessRequestId,
  companyId,
  performedBy,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState('confirm'); // 'confirm' | 'working' | 'done' | 'error'
  const [tempPassword, setTempPassword] = useState(generateSecurePassword());
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [resultMsg, setResultMsg] = useState('');
  const [actionType, setActionType] = useState('');
  const [copied, setCopied] = useState(false);

  const clientNombre = client?.nombre || '—';
  const clientEmail = client?.email || '';
  const clientTelefono = client?.telefono || '';
  const hasAuthAccess = !!client?.portal_user_id;

  const handleCopy = (pwd) => {
    navigator.clipboard?.writeText(pwd);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleConfirm = async () => {
    if (!tempPassword || tempPassword?.length < 6) {
      setErrorMsg('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    setStep('working');
    setErrorMsg('');

    try {
      const { supabase } = await import('../../../lib/supabase');

      // ── Step 1: Call manage-portal-user edge function ──────────────────────
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      const { data: sessionData } = await supabase?.auth?.getSession();
      const accessToken = sessionData?.session?.access_token;

      const edgeRes = await fetch(`${supabaseUrl}/functions/v1/manage-portal-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken || supabaseAnonKey}`,
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify({
          action: 'create_or_update',
          email: clientEmail?.trim()?.toLowerCase(),
          password: tempPassword,
          nombre: clientNombre,
        }),
      });

      const edgeResult = await edgeRes?.json();

      if (!edgeResult?.success) {
        throw new Error(edgeResult?.error || 'Error al actualizar contraseña en Auth.');
      }

      const authUserId = edgeResult?.userId;
      const wasCreated = edgeResult?.action === 'created';
      const detectedActionType = wasCreated ? 'create_auth_access' : 'reset_temp_password';
      setActionType(detectedActionType);

      // ── Step 2: If client has no portal_user_id, link it ──────────────────
      if (authUserId && client?.id && !client?.portal_user_id) {
        await supabase?.from('clients')?.update({ portal_user_id: authUserId })?.eq('id', client?.id);
      }

      // ── Step 3: Update portal_access_requests — by ID or by client_id ─────
      const resetPayload = {
        temp_password: tempPassword,
        password_changed_by_client: false,
        temp_password_set_at: new Date()?.toISOString(),
        acceso_creado: true,
        requiere_cambio_password: true,
        password_temporal_activa: true,
        acceso_activado: false,
      };

      if (portalAccessRequestId) {
        await supabase?.from('portal_access_requests')?.update(resetPayload)?.eq('id', portalAccessRequestId);
      } else if (client?.id) {
        // Try to find any access request for this client and mark it
        const { data: existingReqs } = await supabase
          ?.from('portal_access_requests')
          ?.select('id')
          ?.eq('client_id', client?.id)
          ?.order('created_at', { ascending: false })
          ?.limit(1);
        if (existingReqs?.length > 0) {
          await supabase?.from('portal_access_requests')?.update(resetPayload)?.eq('id', existingReqs?.[0]?.id);
        }
      }

      // ── Step 4: Write audit log ────────────────────────────────────────────
      try {
        await supabase?.from('admin_password_reset_log')?.insert({
          company_id: companyId || null,
          client_id: client?.id || null,
          client_nombre: clientNombre,
          client_email: clientEmail || null,
          client_telefono: clientTelefono || null,
          action_type: detectedActionType,
          performed_by: performedBy?.id || null,
          performed_by_nombre: performedBy?.nombre || null,
          performed_at: new Date()?.toISOString(),
          portal_access_request_id: portalAccessRequestId || null,
          notes: wasCreated
            ? 'Acceso auth creado y contraseña temporal asignada desde backoffice'
            : 'Contraseña temporal restablecida desde backoffice',
        });
      } catch (logErr) {
        // Non-fatal — log but don't fail the operation
        console.warn('[AdminResetPasswordModal] audit log insert failed:', logErr?.message);
      }

      // ── Step 5: Success ────────────────────────────────────────────────────
      const msgs = [];
      if (wasCreated) {
        msgs?.push('Acceso al portal activado');
        msgs?.push('Cliente vinculado correctamente');
      }
      msgs?.push('Contraseña temporal generada');
      setResultMsg(msgs?.join(' · '));
      setStep('done');
      onSuccess?.({ tempPassword, actionType: detectedActionType, authUserId });
    } catch (err) {
      console.error('[AdminResetPasswordModal] error:', err);
      setErrorMsg(err?.message || 'Error inesperado. Intenta de nuevo.');
      setStep('error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-3 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Icon name="KeyRound" size={20} className="text-orange-600" />
            </div>
            <div>
              <h2 className="text-base font-heading font-semibold text-foreground">Restablecer contraseña</h2>
              <p className="text-xs text-muted-foreground">Acción de administrador</p>
            </div>
          </div>
          {step !== 'working' && (
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-smooth">
              <Icon name="X" size={18} className="text-muted-foreground" />
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          {/* Client info card */}
          <div className="bg-muted/50 rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Cliente</p>
            <p className="text-base font-bold text-foreground">{clientNombre}</p>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <div>
                <p className="text-xs text-muted-foreground">Teléfono</p>
                <p className="text-sm text-foreground">{clientTelefono || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Correo</p>
                <p className="text-sm text-foreground truncate">{clientEmail || '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs text-muted-foreground">Acceso portal:</p>
              {hasAuthAccess ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                  <Icon name="ShieldCheck" size={11} /> Cuenta auth vinculada
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                  <Icon name="ShieldOff" size={11} /> Sin cuenta auth
                </span>
              )}
            </div>
          </div>

          {/* Step: confirm */}
          {(step === 'confirm' || step === 'error') && (
            <>
              {!clientEmail && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <Icon name="AlertCircle" size={15} className="text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">Este cliente no tiene correo electrónico registrado. No es posible restablecer la contraseña sin un email válido.</p>
                </div>
              )}

              {clientEmail && (
                <>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <Icon name="AlertTriangle" size={15} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Se restablecerá la contraseña del cliente</p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        {hasAuthAccess
                          ? 'Se actualizará la contraseña en Auth y se marcará como temporal.' :'Se creará una cuenta auth para este cliente y se asignará la contraseña temporal.'}
                      </p>
                    </div>
                  </div>

                  {/* Password field */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Contraseña temporal</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={tempPassword}
                          onChange={(e) => setTempPassword(e?.target?.value)}
                          placeholder="Mínimo 6 caracteres"
                          className="w-full pl-3 pr-10 py-2.5 border border-border rounded-lg text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(v => !v)}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={15} />
                        </button>
                      </div>
                      {/* Copy button */}
                      <button
                        type="button"
                        onClick={() => handleCopy(tempPassword)}
                        disabled={!tempPassword}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-smooth whitespace-nowrap ${
                          copied
                            ? 'bg-green-100 text-green-700 border-green-300' :'bg-muted text-foreground hover:bg-muted/80 border-border'
                        }`}
                        title="Copiar contraseña"
                      >
                        <Icon name={copied ? 'Check' : 'Copy'} size={13} />
                        {copied ? 'Copiada' : 'Copiar'}
                      </button>
                      {/* Generate button */}
                      <button
                        type="button"
                        onClick={() => { setTempPassword(generateSecurePassword()); setCopied(false); }}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-muted text-foreground hover:bg-muted/80 border border-border transition-smooth whitespace-nowrap"
                        title="Generar contraseña segura"
                      >
                        <Icon name="Shuffle" size={13} />
                        Generar
                      </button>
                    </div>
                    {tempPassword && (
                      <p className={`text-xs flex items-center gap-1 ${tempPassword?.length >= 6 ? 'text-green-600' : 'text-red-500'}`}>
                        <Icon name={tempPassword?.length >= 6 ? 'CheckCircle2' : 'AlertCircle'} size={12} />
                        {tempPassword?.length >= 6 ? `Longitud válida (${tempPassword?.length} caracteres)` : 'Mínimo 6 caracteres'}
                      </p>
                    )}
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <Icon name="Info" size={11} />
                      El cliente deberá cambiar esta contraseña en su próximo inicio de sesión.
                    </p>
                  </div>

                  {errorMsg && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
                      <Icon name="AlertCircle" size={14} />
                      {errorMsg}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Step: working */}
          {step === 'working' && (
            <div className="flex flex-col items-center justify-center py-6 gap-3">
              <Icon name="Loader2" size={32} className="text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">Procesando restablecimiento...</p>
            </div>
          )}

          {/* Step: done */}
          {step === 'done' && (
            <div className="space-y-3">
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
                <Icon name="CheckCircle2" size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-green-800">{resultMsg}</p>
                  <p className="text-xs text-green-700 mt-1">La acción ha sido registrada en la bitácora interna.</p>
                </div>
              </div>
              <div className="p-3 bg-muted rounded-lg space-y-1">
                <p className="text-xs text-muted-foreground font-medium">Contraseña temporal asignada:</p>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-mono font-bold text-foreground flex-1 select-all">{tempPassword}</p>
                  <button
                    onClick={() => handleCopy(tempPassword)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-smooth ${
                      copied
                        ? 'bg-green-100 text-green-700 border-green-300' :'bg-background text-muted-foreground hover:text-foreground border-border hover:bg-muted/80'
                    }`}
                    title="Copiar contraseña"
                  >
                    <Icon name={copied ? 'Check' : 'Copy'} size={13} />
                    {copied ? 'Copiada' : 'Copiar'}
                  </button>
                </div>
                <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                  <Icon name="AlertCircle" size={11} />
                  El cliente deberá cambiarla al primer inicio de sesión.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-5 border-t border-border">
          {step === 'done' ? (
            <button
              onClick={onClose}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-smooth"
            >
              <Icon name="Check" size={15} />
              Listo
            </button>
          ) : step === 'working' ? (
            <div className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-muted text-muted-foreground cursor-not-allowed">
              <Icon name="Loader2" size={15} className="animate-spin" />
              Procesando...
            </div>
          ) : (
            <>
              <button
                onClick={onClose}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-muted text-foreground hover:bg-muted/80 border border-border transition-smooth"
              >
                Cancelar
              </button>
              {clientEmail && (
                <button
                  onClick={handleConfirm}
                  disabled={!tempPassword || tempPassword?.length < 6}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-orange-600 text-white hover:bg-orange-700 transition-smooth disabled:opacity-50"
                >
                  <Icon name="KeyRound" size={15} />
                  Restablecer contraseña
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminResetPasswordModal;
